import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { bubble, trigger } from './Tooltip.css'

export type TooltipProps = {
  content: ReactNode
  children: ReactNode
  placement?: 'top' | 'bottom'
}

type Coords = {
  top: number
  left: number
  placement: 'top' | 'bottom'
}

const SHOW_DELAY_MS = 120

export function Tooltip({ content, children, placement = 'top' }: TooltipProps) {
  const tooltipId = useId()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [coords, setCoords] = useState<Coords | null>(null)

  const place = useCallback(() => {
    const el = anchorRef.current
    if (el === null) {
      return
    }
    const rect = el.getBoundingClientRect()
    const resolved = placement === 'top' && rect.top < 64 ? 'bottom' : placement
    setCoords({
      top: resolved === 'top' ? rect.top : rect.bottom,
      left: rect.left + rect.width / 2,
      placement: resolved,
    })
  }, [placement])

  const hide = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current)
    }
    setCoords(null)
  }, [])

  const showAfterDelay = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(place, SHOW_DELAY_MS)
  }, [place])

  useEffect(() => {
    if (coords === null) {
      return
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        hide()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [coords, hide])

  useEffect(
    () => () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current)
      }
    },
    [],
  )

  return (
    <>
      <span
        ref={anchorRef}
        className={trigger}
        tabIndex={0}
        aria-describedby={coords !== null ? tooltipId : undefined}
        onMouseEnter={showAfterDelay}
        onMouseLeave={hide}
        onFocus={place}
        onBlur={hide}
      >
        {children}
      </span>
      {coords !== null
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              className={bubble({ placement: coords.placement })}
              style={{ top: coords.top, left: coords.left }}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
